import React from "react";
import Card from "components/card";
import TimePicker from "react-time-picker";
import Button from "components/button/Button";
import TextField from "components/fields/TextField";
import InputField from "components/fields/InputField";
import useLocationArea from "hooks/locationArea/useLocationArea";
import SelectAsyncField from "components/fields/SelectAsyncField";
import LocationMarker from "components/maps/leaflet/LocationMarker";
import { AxiosError, AxiosResponse } from "axios";
import { toast } from "react-hot-toast";
import { MapContainer, TileLayer } from "react-leaflet";
import { useNavigate, useParams } from "react-router-dom";
import { addBranch, getBranch, updateBranch } from "api/BranchAPI";
import {
  Controller,
  FieldValues,
  SubmitHandler,
  useForm,
} from "react-hook-form";

import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
import "leaflet/dist/leaflet.css";
import { Marker } from "leaflet";
import { ICity, IProvince } from "./types/branch.interface";
import { BiFullscreen } from "react-icons/bi";
import { BsFullscreenExit } from "react-icons/bs";
import { errMessageMapper } from "utils";

const BranchesForm: React.FC = () => {
  const navigate = useNavigate();
  const defaultTime = "00:00";
  const markerRef = React.useRef<Marker>(null);
  const initialPosition: [number, number] = [-7.257471, 112.752088];
  const { slug } = useParams<{ slug: string }>();
  const {
    getProvinces,
    getSelectedProvince,
    getSelectedCity,
    loadAsyncProvinces,
    loadAsyncCities,
    selectedProvince,
    selectedCity,
    handleOnProvinceChange,
    handleOnCityChange,
  } = useLocationArea();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [position, setPosition] = React.useState<[number, number]>();
  const [isBigMap, setIsBigMap] = React.useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  const handleSetPosition = (lat: number, lng: number) => {
    setPosition([lat, lng]);
  };

  const onSubmit: SubmitHandler<FieldValues> = (data) => {
    if (!position) {
      toast.error("Pin lokasi toko harus dipilih!");
      return;
    }

    setIsLoading(true);

    const payload = {
      ...data,
      name: data?.name,
      address: data?.address || "",
      latitude: position?.[0] || 0,
      longitude: position?.[1] || 0,
      open_at: data?.open_at,
      close_at: data?.close_at,
      is_open: true,
      is_active: true,
      province_id: selectedProvince.id,
      city_id: selectedCity.id,
      cs_phone: data?.cs_phone,
    };

    let promise = null;
    if (!slug) promise = addBranch(payload);
    else promise = updateBranch(slug, payload);

    toast.promise(promise, {
      loading: !slug ? "Menambahkan toko..." : "Menyimpan toko...",
      success: (res) => {
        if (res.status === 201) {
          navigate("/admin/branches");
          return "Toko berhasil ditambahkan!";
        } else if (res.status === 200) {
          navigate(-1);
          return "Toko berhasil disimpan!";
        }

        setIsLoading(false);
      },
      error: (err: AxiosError) => {
        setIsLoading(false);
        return errMessageMapper(err) || "Gagal menambahkan toko!";
      },
    });
  };

  const getBranchDetail = () => {
    const promise = getBranch(slug);

    toast.promise(promise, {
      loading: "Mengambil data toko...",
      success: (res) => {
        mapBranchData(res);
        return res?.data?.message || "Berhasil mengambil data toko!";
      },
      error: "Gagal mengambil data toko!",
    });
  };

  const mapBranchData = async (res: AxiosResponse<any, any>) => {
    const data = res?.data?.data;
    const selectedProvince = await getSelectedProvince(data?.province_id);
    const selectedCity = await getSelectedCity(
      data?.city_id,
      data?.province_id
    );

    setPosition([data?.latitude, data?.longitude]);
    reset({
      name: data?.name,
      open_at: data?.open_at,
      close_at: data?.close_at,
      address: data?.address,
      cs_phone: data?.cs_phone,
      province: selectedProvince,
      city: selectedCity,
    });
  };

  React.useEffect(() => {
    if (slug) {
      getProvinces();
      getBranchDetail();
    }

    // eslint-disable-next-line
  }, []);

  return (
    <Card extra={"w-full h-full sm:overflow-auto px-6 pb-3 mt-3"}>
      <header className="relative flex items-center justify-between pt-4">
        <div className="text-xl font-bold text-navy-700 dark:text-white">
          Tambah Toko
        </div>
      </header>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mt-8">
          <div className="mb-3 grid w-full grid-cols-12 gap-3">
            <InputField
              extra="w-full col-span-12 lg:col-span-6"
              label="Nama Toko"
              placeholder="Masukkan nama toko"
              id="name"
              type="text"
              variant="outline"
              register={register}
              errors={errors}
              validationSchema={{
                required: "Nama Toko harus diisi!",
              }}
            />
            <InputField
              extra="w-full col-span-12 lg:col-span-6"
              label="Nomor Telepon CS"
              placeholder="08xxxx atau +628xxxx"
              id="cs_phone"
              type="tel"
              variant="outline"
              register={register}
              errors={errors}
              validationSchema={{
                pattern: {
                  value: /^(^\+62\s?|^0)(\d{3,4}-?){2}\d{3,4}$/g,
                  message: "Nomor telepon tidak valid!",
                },
              }}
            />
          </div>
          <div className="mb-3 flex w-full gap-3">
            <div className="flex-1 flex w-full flex-col gap-3">
              <label htmlFor="open_at" className="label required">
                Jam Buka
              </label>
              <Controller
                name="open_at"
                control={control}
                defaultValue={defaultTime}
                render={({ field }) => (
                  <TimePicker
                    disableClock={true}
                    format="HH:mm"
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </div>
            <div className="flex-1 flex w-full flex-col gap-3">
              <label htmlFor="close_at" className="label required">
                Jam Tutup
              </label>
              <Controller
                name="close_at"
                control={control}
                defaultValue={defaultTime}
                render={({ field }) => (
                  <TimePicker
                    disableClock={true}
                    format="HH:mm"
                    onChange={field.onChange}
                    value={field.value}
                  />
                )}
              />
            </div>
          </div>
          <div className="mb-3 mt-5">
            <label htmlFor="address" className="label required">
              Pin Lokasi Toko
            </label>
            <section
              className={`relative z-[1] mt-3 w-full overflow-hidden transition-all duration-300 ${
                isBigMap ? "h-[570px]" : "h-96"
              }`}
            >
              <MapContainer
                center={
                  position ? position : [initialPosition[0], initialPosition[1]]
                }
                zoom={13}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%" }}
                className="rounded-md"
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <LocationMarker
                  draggable={true}
                  ref={markerRef}
                  position={position ? position : initialPosition}
                  setPosition={handleSetPosition}
                />
              </MapContainer>
              <div className="absolute right-0 top-1 z-[999] rounded-2xl">
                <Button
                  size="fit"
                  variant="text-only"
                  onClick={() => setIsBigMap(!isBigMap)}
                  type="button"
                  children={
                    isBigMap ? (
                      <BsFullscreenExit className="bg-brand-500 p-2 text-4xl text-white dark:bg-white dark:text-navy-700" />
                    ) : (
                      <BiFullscreen className="bg-brand-500 p-2 text-4xl text-white dark:bg-white dark:text-navy-700" />
                    )
                  }
                />
              </div>
            </section>
          </div>
          <div className="mb-3 grid w-full grid-cols-12 gap-3">
            <Controller
              name="province"
              control={control}
              rules={{ required: "Provinsi harus dipilih!" }}
              render={({ field: { value, onChange, ref } }) => (
                <SelectAsyncField
                  loadOptions={loadAsyncProvinces}
                  onInputChange={(value: IProvince) => {
                    handleOnProvinceChange(value);
                    setValue("city", null);
                    onChange(value);
                    loadAsyncCities(value?.id);
                  }}
                  value={value}
                  isMulti={false}
                  id="province"
                  selectRef={ref}
                  label="Provinsi"
                  placeholder="Pilih Provinsi"
                  extra="flex-1 w-full col-span-12 lg:col-span-6"
                  errors={errors}
                  rules={{
                    required: "Provinsi harus dipilih!",
                  }}
                />
              )}
            />
            <Controller
              name="city"
              control={control}
              rules={{ required: "Kota harus dipilih!" }}
              render={({ field: { value, onChange, ref } }) => (
                <SelectAsyncField
                  loadOptions={() => loadAsyncCities(selectedProvince?.id)}
                  onInputChange={(value: ICity) => {
                    handleOnCityChange(value);
                    onChange(value);
                  }}
                  value={value}
                  isMulti={false}
                  id="city"
                  label="Kota"
                  selectRef={ref}
                  placeholder="Pilih Kota"
                  extra="flex-1 w-full col-span-12 lg:col-span-6"
                  disabled={!getValues("province")}
                  errors={errors}
                  rules={{
                    required: "Kota harus dipilih!",
                  }}
                />
              )}
            />
          </div>
          <TextField
            extra="mb-3 flex-1"
            label="Alamat Lengkap Toko"
            placeholder="Masukkan alamat toko"
            id="address"
            variant="outline"
            defaultValue={getValues("address")}
            rows={3}
            register={register}
            errors={errors}
            validationSchema={{}}
          />
        </div>
        <Button
          children="Simpan"
          size="fit"
          variant="primary"
          extra="mt-7 mb-5"
          isLoading={isLoading}
        />
      </form>
    </Card>
  );
};

export default BranchesForm;
